"use client";
import React from "react";
import { Caveat } from "next/font/google";
import Image from "next/image";
import { motion } from "framer-motion";

const caveat = Caveat({ subsets: ["latin"], weight: ["400", "700"], display: "swap" });

export default function FitnessSalesRepsSection({ section }) {
  const title = section?.title || "Make your sales reps count";
  const mainText = section?.mainText || "<strong className='text-white font-semibold'>Grow your customer base</strong> by converting emails and website forms into leads. Use the centralized CRM pipeline to ensure you never miss a follow-up.";
  const quoteText = section?.quoteText || "All I need to do is host the class!";
  const avatarImage = section?.avatarImage || "/odoo/snippets/s_wd_testimonials/avatar/wim_van_den_brande.webp";
  const crmImage = section?.crmImage || "/odoo/industries/fitness/crm-pipeline.png";

  return (
    <section className="py-20 lg:py-24 px-6 sm:px-12 lg:px-24 relative overflow-visible bg-transparent font-sans text-white w-full z-10">
      <div className="container mx-auto max-w-[1400px]">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          
          {/* Left Column: Direct Image matching user reference screenshot */}
          <motion.div 
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-6 relative flex justify-center items-center"
          >
            {/* Direct CRM Pipeline Image */}
            <Image
              src={crmImage}
              alt="Centralized CRM pipeline interface"
              width={992}
              height={566}
              className="w-full h-auto object-contain transition-transform duration-300 hover:scale-[1.01]"
              priority
            />
          </motion.div>

          {/* Right Column: Heading, Text & Testimonial Chip */}
          <motion.div 
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-6 space-y-8"
          >
            {/* Heading */}
            <h2 className={`text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-tight ${caveat.className}`}>
              <span>Make your sales reps </span>
              <span className="relative inline-block pb-2 text-white">
                count
                <svg 
                  className="absolute bottom-0 left-0 w-full h-3.5 text-teal-400 overflow-visible" 
                  viewBox="0 0 100 20" 
                  fill="none" 
                  preserveAspectRatio="none"
                >
                  <path 
                    d="M 5 14 Q 50 2, 95 12 T 190 12" 
                    stroke="currentColor" 
                    strokeWidth="6" 
                    strokeLinecap="round"
                  />
                </svg>
              </span>
            </h2>

            {/* Subtext */}
            <p 
              className="text-slate-300 text-lg md:text-xl leading-relaxed font-normal pt-2"
              dangerouslySetInnerHTML={{ __html: mainText }}
            />

            {/* Testimonial Chip */}
            <div className="pt-4 flex items-center gap-4">
              <div className="relative w-14 h-14 rounded-full overflow-hidden border-2 border-white shadow-lg flex-shrink-0">
                <Image
                  src={avatarImage}
                  alt="Fitness Instructor"
                  fill
                  className="object-cover"
                />
              </div>

              <div className="bg-white text-slate-900 px-6 py-3.5 rounded-full shadow-xl border border-slate-100 text-sm font-medium italic flex items-center">
                "{quoteText}"
              </div>
            </div>

          </motion.div>

        </div>
      </div>
    </section>
  );
}
